import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ClipboardList, Home, PackageSearch, PhoneCall, Truck, XCircle } from "lucide-react";
import { FlameMark } from "../components/Logo";
import { formatINR } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [
  { key: "PLACED", label: "Placed", icon: ClipboardList, desc: "Order received" },
  { key: "CONFIRMED", label: "Confirmed", icon: PhoneCall, desc: "Verified by call" },
  { key: "DISPATCHED", label: "Dispatched", icon: Truck, desc: "On the way" },
  { key: "DELIVERED", label: "Delivered", icon: Home, desc: "Celebrate!" },
];

function formatApiErrorDetail(detail) {
  if (typeof detail === "string") return detail;
  return "We couldn't find that order. Double-check the reference number.";
}

export default function Track() {
  const [params] = useSearchParams();
  const [num, setNum] = useState(params.get("uc") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (value) => {
    const q = String(value ?? num).trim().toUpperCase();
    if (!q) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/orders/${encodeURIComponent(q)}`);
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(formatApiErrorDetail(data?.detail));
      setOrder(data);
    } catch (e) {
      setError(e.message);
      setOrder(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const uc = params.get("uc");
    if (uc) lookup(uc);
  }, []);

  const cancelled = order?.status === "CANCELLED";
  const currentIdx = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <div className="min-h-screen bg-obsidian text-starlight" data-testid="track-page">
      <header className="border-b border-starlight/10 bg-card/40">
        <div className="mx-auto max-w-3xl px-4 sm:px-8 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <FlameMark className="h-8 w-8" />
            <span className="leading-none">
              <span className="block font-display font-bold tracking-[0.18em] text-starlight text-lg">UTSAV</span>
              <span className="block font-mono text-[10px] tracking-[0.42em] text-ember mt-0.5">CRACKERS</span>
            </span>
          </a>
          <a
            href="/"
            className="rounded-full border border-starlight/15 px-5 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night hover:border-ember/60 hover:text-ember transition-colors"
          >
            Store
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 sm:px-8 py-14">
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Track your crackers</h1>
        <p className="mt-2 text-sm text-muted-night">
          Enter the reference number from your order confirmation — it starts with UC.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            lookup();
          }}
          className="mt-8 flex flex-col sm:flex-row gap-3"
        >
          <input
            data-testid="track-order-input"
            value={num}
            onChange={(e) => setNum(e.target.value.toUpperCase())}
            placeholder="UC2B0A053C"
            className="flex-1 rounded-full border border-starlight/15 bg-white/5 px-6 py-3.5 font-mono text-sm text-starlight placeholder:text-muted-night/50 outline-none focus:border-ember/70"
          />
          <button
            data-testid="track-order-button"
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-ember px-8 py-3.5 font-mono text-xs font-bold tracking-[0.22em] uppercase text-obsidian hover:bg-marigold transition-colors disabled:opacity-50"
          >
            <PackageSearch className="h-4 w-4" />
            {busy ? "Looking…" : "Track order"}
          </button>
        </form>

        {error && (
          <p className="mt-6 rounded-2xl border border-crimson/40 bg-crimson/10 px-5 py-4 text-sm text-starlight" data-testid="track-error">
            {error}
          </p>
        )}

        {order && (
          <div className="mt-8 rounded-3xl border border-ember/25 bg-card p-6 sm:p-8" data-testid="track-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-xl font-bold text-ember">{order.order_number}</p>
                <p className="font-mono text-[10px] text-muted-night mt-0.5">
                  Placed {new Date(order.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                </p>
              </div>
              <p className="font-mono text-lg font-bold text-starlight">
                {formatINR(order.total)} <span className="text-[10px] text-muted-night font-normal">COD</span>
              </p>
            </div>

            {cancelled ? (
              <div className="mt-6 flex items-center gap-3 rounded-2xl border border-crimson/40 bg-crimson/10 px-5 py-4" data-testid="track-cancelled">
                <XCircle className="h-5 w-5 text-crimson" />
                <div>
                  <p className="font-semibold text-starlight text-sm">This order was cancelled</p>
                  <p className="text-xs text-muted-night">If this is a surprise, call the shop to sort it out.</p>
                </div>
              </div>
            ) : (
              <div className="mt-8" data-testid="track-timeline">
                <div className="flex items-start">
                  {STEPS.map((step, i) => {
                    const done = i <= currentIdx;
                    const isLast = i === STEPS.length - 1;
                    return (
                      <div key={step.key} className={`flex-1 ${isLast ? "" : "pr-2"}`}>
                        <div className="flex items-center">
                          <span
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${
                              done ? "border-ember bg-ember/15 text-ember" : "border-starlight/15 text-muted-night"
                            }`}
                          >
                            {done && i === currentIdx ? <CheckCircle2 className="h-5 w-5" /> : <step.icon className="h-4.5 w-4.5" />}
                          </span>
                          {!isLast && (
                            <span className={`h-0.5 flex-1 mx-1 rounded ${i < currentIdx ? "bg-ember" : "bg-starlight/15"}`} />
                          )}
                        </div>
                        <p className={`mt-2.5 text-xs font-semibold ${done ? "text-starlight" : "text-muted-night"}`}>{step.label}</p>
                        <p className="text-[10px] text-muted-night">{step.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-starlight/10 pt-6 text-sm">
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">Delivering to</p>
                <p className="mt-1 font-semibold text-starlight">{order.customer.name}</p>
                <p className="text-muted-night">
                  {order.customer.address} — {order.customer.pincode}
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-night">
                  Items ({order.items.reduce((s, i) => s + i.qty, 0)})
                </p>
                <p className="mt-1 text-muted-night leading-relaxed">
                  {order.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
